import type { ComponentChildren } from 'preact'
import { useRef, useState } from 'preact/hooks'
import styles from './InfoButton.module.css'
import { InfoSheet } from './InfoSheet'

/**
 * The control that opens an explanation (D-104 §2).
 *
 * **It owns its own sheet.** A caller writes the explanation as children and
 * nothing else — no open state to thread, no id to keep unique. That is what
 * keeps the pattern cheap enough to use everywhere it belongs, which is the
 * difference between this record being applied and being approximated.
 *
 * **The glyph is decorative and the name is the topic.** "About confidence",
 * never "i" or "info" — a screen reader hearing "info button" eleven times on
 * one screen has learned nothing. `topic` is the noun the explanation is about,
 * and it must be distinct from the surrounding control names: Playwright matches
 * accessible names by substring, so a name containing a screen or tab name will
 * collide with specs that never mention it.
 *
 * **Never inside an `h1`.** Content inside a heading joins its accessible name,
 * and four specs find Today by `getByRole('heading', { name: 'Today' })`.
 *
 * **The hit area is 44 × 44 px and the glyph is not.** Padding grows the target
 * and a negative margin absorbs it, so a control that must be comfortable with a
 * thumb does not push the line it sits on out of shape.
 */
interface InfoButtonProps {
  /** The noun this explains, used as "About {topic}". Not a sentence. */
  topic: string
  /** The sheet's visible title. Defaults to the topic, capitalised by the caller. */
  title?: string
  children: ComponentChildren
}

export function InfoButton({ topic, title, children }: InfoButtonProps) {
  const [open, setOpen] = useState(false)
  /* Handed to the sheet so focus comes back here, rather than being inferred
     from `document.activeElement` — which is `<body>` on iOS, because tapping a
     button does not focus it there. */
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        class={styles.button}
        aria-label={`About ${topic}`}
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        {/* Decorative: the accessible name is on the button. */}
        <svg
          class={styles.glyph}
          viewBox="0 0 20 20"
          width="20"
          height="20"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="10" cy="10" r="8.25" fill="none" stroke="currentColor" stroke-width="1.5" />
          <circle cx="10" cy="6.1" r="1.05" fill="currentColor" />
          <path
            d="M10 9.1v5.2"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          />
        </svg>
      </button>

      <InfoSheet
        open={open}
        title={title ?? topic}
        onClose={() => setOpen(false)}
        returnFocusTo={buttonRef}
      >
        {children}
      </InfoSheet>
    </>
  )
}
